import React, { Component } from 'react'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  Stack,
  Typography,
} from '@mui/material'
import {
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material'
import api from '../../lib/api'
import { getStoredUser, isAuthenticated } from '../../services/auth'

class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    }
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary capturou um erro:', error, errorInfo)

    this.setState({ error, errorInfo })

    if (this.props.logToBackend) {
      this.logErrorToBackend(error, errorInfo)
    }
  }

  logErrorToBackend = async (error, errorInfo) => {
    try {

      if (!isAuthenticated()) {
        console.warn('ErrorBoundary: log ignorado (usuário não autenticado)', error)
        return
      }

      const user = getStoredUser()
      const errorData = {
        message: error.toString(),
        stack: error.stack,
        componentStack: errorInfo?.componentStack,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        userId: user?.profile?.id || user?.id,
        enterpriseId: user?.profile?.enterprise?.id || user?.enterprise?.id,
        componentName: this.props.componentName || 'Unknown',
        severity: 'error',
      }

      await api.post('frontend-errors/', {
        body: JSON.stringify(errorData),
      }).catch((err) => {
        console.warn('Não foi possível registrar o erro no backend:', err)
      })
    } catch (err) {
      console.warn('Falha ao registrar erro no backend:', err)
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    })

    if (this.props.onReset) {
      this.props.onReset()
    }
  }

  toggleDetails = () => {
    this.setState((prev) => ({ showDetails: !prev.showDetails }))
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback({
          error: this.state.error,
          errorInfo: this.state.errorInfo,
          resetError: this.handleReset,
        })
      }

      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: this.props.fullScreen ? '100vh' : '200px',
            p: 2,
          }}
        >
          <Card elevation={3} sx={{ maxWidth: 600, width: '100%' }}>
            <CardContent>
              <Stack spacing={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <ErrorIcon color="error" fontSize="large" />
                  <Typography variant="h5" component="h2" color="error">
                    {this.props.title || 'Algo deu errado'}
                  </Typography>
                </Box>

                <Alert severity="error">
                  <AlertTitle>Erro</AlertTitle>
                  {this.props.message ||
                    'Ocorreu um erro inesperado. Por favor, tente novamente.'}
                </Alert>

                {this.state.error && (
                  <>
                    <Button
                      size="small"
                      onClick={this.toggleDetails}
                      endIcon={
                        <ExpandMoreIcon
                          sx={{
                            transform: this.state.showDetails
                              ? 'rotate(180deg)'
                              : 'rotate(0deg)',
                            transition: 'transform 0.3s',
                          }}
                        />
                      }
                    >
                      {this.state.showDetails ? 'Ocultar Detalhes' : 'Mostrar Detalhes'}
                    </Button>

                    <Collapse in={this.state.showDetails}>
                      <Box
                        sx={{
                          backgroundColor: '#f5f5f5',
                          p: 2,
                          borderRadius: 1,
                          overflow: 'auto',
                          maxHeight: 300,
                        }}
                      >
                        <Typography
                          variant="body2"
                          component="pre"
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {this.state.error.toString()}
                          {'\n\n'}
                          {this.state.error.stack}
                          {'\n\nComponent Stack:'}
                          {this.state.errorInfo?.componentStack}
                        </Typography>
                      </Box>
                    </Collapse>
                  </>
                )}

                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  {this.props.showReload && (
                    <Button
                      variant="outlined"
                      onClick={() => window.location.reload()}
                    >
                      Recarregar Página
                    </Button>
                  )}
                  {this.props.showReset !== false && (
                    <Button
                      variant="contained"
                      startIcon={<RefreshIcon />}
                      onClick={this.handleReset}
                    >
                      Tentar Novamente
                    </Button>
                  )}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      )
    }

    return this.props.children
  }
}

ErrorBoundary.defaultProps = {
  logToBackend: false,
  showReset: true,
  showReload: false,
  fullScreen: false,
}

export default ErrorBoundary
